import { desanitizeKey, parseEmoji } from '@/helper/utils';
import { AppDispatch, RootState } from '@/store';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import { AdminUser, SiteFormData } from '@/types/project.types';
import { Check, Expand } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface ReviewAndCreateProps {
  projectId?: number;
  projectName: string;
  sites: SiteFormData[];
  primaryColor: string;
  secondaryColor: string;
  selectedEmoji: string;
  description: string;
  admins: AdminUser[];
  isConfirmed: boolean;
  setIsConfirmed: (value: boolean) => void;
  isEditMode: boolean;
}

const ReviewAndCreate: React.FC<ReviewAndCreateProps> = ({
  projectId,
  projectName,
  isEditMode,
  sites,
  primaryColor,
  secondaryColor,
  selectedEmoji,
  description,
  admins,
  isConfirmed,
  setIsConfirmed,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { countries } = useSelector((state: RootState) => state.metaData);
  const { roles } = useSelector((state: RootState) => state.user);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const getRoleName = (roleId: string | number) => {
    const role = roles.find((r) => r.id === Number(roleId));
    return role?.name || 'Unknown Role';
  };

  const projectCountries = useMemo(() => {
    const uniqueCountryIds = new Set(sites.map((s) => s.countryId).filter((id) => id > 0));
    const countryNames = Array.from(uniqueCountryIds)
      .map((id) => countries.find((c) => c.id === id)?.name)
      .filter(Boolean);

    return countryNames.length > 0 ? countryNames.join(', ') : '—';
  }, [sites, countries]);

  useEffect(() => {
    dispatch(fetchMetaData());
  }, [dispatch]);

  return (
    <div className="mx-auto text-[#621C43]">
      {/* Header */}
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">Review & {isEditMode ? 'Update' : 'Create'}</h2>
        <p className="text-base font-medium">
          Review all information before {isEditMode ? 'updating the' : 'creating the new'} project
        </p>
      </div>

      <div className="px-5 py-2">
        <div className="grid md:grid-cols-2 gap-2">
          {/* Basic Information */}
          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Basic Information</h3>
            <div className="text-sm font-medium space-y-1">
              <div className="flex justify-between items-center">
                <span className="font-light">Project Name </span>
                <span className="font-medium">{projectName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-light">Project Countries </span>
                <span className="font-medium">{projectCountries}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-light">Max. number of Sites </span>
                <span className="font-medium">{sites.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="block font-light">Site Location(s)</span>
                <ul className="list-disc pl-5 text-sm capitalize">
                  {sites.map((s, i) => {
                    const country = countries.find((c) => c.id === s.countryId);

                    return (
                      <li key={i}>
                        {s.name || '—'} ({country?.name || '—'})
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Visual & Messaging  */}
          <div className="border-[0.5px] border-[#000] rounded-xl p-3 px-4">
            <h3 className="font-medium mb-4 text-base">Visual & Messaging</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {/* left */}
              <div className="bg-[#F2EBEE] p-3 rounded-md flex flex-col justify-start items-start overflow-hidden">
                <h4 className="text-md text-[#2B1C25]">{projectId ? projectId : 'P-XXX'}</h4>
                <h1 className="text-xl font-bold text-[#2B1C25] truncate">{projectName}</h1>
                <div className="w-full gap-3 text-3xl mt-4 flex justify-between items-center">
                  <div className="bg-[#AFAFAF80] w-14 h-14 rounded-lg flex items-center justify-center text-4xl">
                    {parseEmoji(selectedEmoji)}
                  </div>

                  <Expand
                    strokeWidth={1}
                    className="bg-[#AFAFAF80] w-14 h-14 p-3 rounded-lg flex items-center justify-center text-[#2B1C25]"
                  />
                </div>
              </div>
              {/* right */}
              <div className="flex flex-col justify-between">
                <div className="text-sm font-medium">
                  {description ? (
                    <>
                      <p>
                        {isDescriptionExpanded || description.length <= 100
                          ? description
                          : `${description.substring(0, 100)}...`}
                      </p>
                      {description.length > 100 && (
                        <button
                          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                          className="text-pink-600 hover:text-pink-800 text-xs mt-1 underline"
                        >
                          {isDescriptionExpanded ? 'Read less' : 'Read more'}
                        </button>
                      )}
                    </>
                  ) : (
                    <p>No description</p>
                  )}
                </div>
                <div className="space-y-2">
                  {primaryColor && (
                    <div
                      className="w-full p-1 px-2 rounded-xl uppercase gap-2 items-center flex text-[#2B1C25] text-xs font-mono"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Check size={15} />
                      <span>{primaryColor}</span>
                    </div>
                  )}
                  {secondaryColor && (
                    <div
                      className="w-full p-1 px-2 rounded-xl uppercase gap-2 items-center flex text-[#2B1C25] text-xs font-mono"
                      style={{ backgroundColor: secondaryColor }}
                    >
                      <Check size={15} />
                      <span> {secondaryColor}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Users */}
        <div className="grid md:grid-cols-3 gap-4 mt-3">
          {admins.map((admin, i) => (
            <div key={i} className="border-[0.5px] border-[#000] rounded-xl p-3 px-4 text-sm ">
              <h3 className="font-medium mb-4 text-base">Admin {i + 1}</h3>
              <div className="text-sm font-medium space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-light">Role</span>
                  <span className="font-medium">
                    {desanitizeKey(getRoleName(admin.roleId)) || '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-light">Phone Number</span>
                  <span className="font-medium">{admin.phoneNumber || '—'}</span>
                </div>
                {/* <div className="flex justify-between items-center">
                  <span className="font-light">User Name</span>
                  <span className="font-medium">{admin.blueNumberId}</span>
                </div> */}
              </div>
            </div>
          ))}
        </div>

        {/* Confirmation */}
        <div className="mt-3 bg-[#4A082D33] border rounded-lg p-3 px-4">
          <input
            id="projectCheckbox"
            type="checkbox"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            className="cursor-pointer mt-1 accent-pink-600 mr-2 bg-transparent"
          />
          <label
            className="cursor-pointer text-sm text-[#621C43] font-medium"
            htmlFor="projectCheckbox"
          >
            I confirm that all the information provided is accurate and I want to{' '}
            {isEditMode ? 'update' : 'create'} this project.
            <br />
            <span className="text-[#621C43] text-xs italic">
              This action will {isEditMode ? 'update the project' : 'create a new project'} with the
              specifications listed above.
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default ReviewAndCreate;
